import {DataStatus, FormSubmitHandler, Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  CommentCardForm,
  CommentCardItem,
  CommentsCardInput,
  handleApiError,
  useWorkshopInterestEditPermissions,
} from '@dms/shared';

type WorkshopInterestCommentsTabProps = {
  interestId: string;
};

export function WorkshopInterestCommentsTab(props: WorkshopInterestCommentsTabProps) {
  const resourceId = EntityResourceIds.workshopInterest;
  const recordId = props.interestId;

  const {
    data: comments,
    isLoading: isFetchingLoading,
    isError,
  } = useGetCommentsQuery({resourceId, recordId});

  const [createComment, {isLoading: isCreateLoading}] = useCreateCommentMutation();
  const [deleteComment, {isLoading: isDeleteLoading}] = useDeleteCommentMutation();
  const [updateComment, {isLoading: isUpdateLoading}] = useUpdateCommentMutation();

  const {hasAddCommentPermission, hasDeleteCommentPermission, hasUpdateCommentPermission} =
    useWorkshopInterestEditPermissions({interestId: props.interestId});

  const isLoading = isFetchingLoading || isCreateLoading || isDeleteLoading || isUpdateLoading;

  const onCommentSubmit: FormSubmitHandler<CommentCardForm> = (values, _, reset) =>
    createComment({
      resourceId,
      recordId,
      createCommentRequestBody: {text: values.comment},
    })
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);

  const handleCommentDelete = (commentId: string) => {
    deleteComment({resourceId, recordId, commentId}).unwrap().catch(handleApiError);
  };

  const handleCommentEdit = (commentId: string, text: string) =>
    updateComment({resourceId, recordId, commentId, updateCommentRequestBody: {text}})
      .unwrap()
      .catch(handleApiError);

  return (
    <VStack spacing={4} grow={1} minHeight={0}>
      <div css={{flex: 1, overflow: 'auto', minHeight: 0}}>
        <DataStatus
          minHeight={35}
          isLoading={isLoading}
          isError={isError}
          isEmpty={!comments?.length}
          emptyMessage={i18n.t('page.comments.labels.noOneCommented')}
        >
          {comments?.map((comment, index) => (
            <VStack key={comment.id}>
              <CommentCardItem
                comment={comment}
                isDeleteCommentLoading={isLoading}
                isEditCommentLoading={isLoading}
                isDeleteDisabled={!hasDeleteCommentPermission}
                isEditDisabled={!hasUpdateCommentPermission}
                onDelete={handleCommentDelete}
                onEdit={handleCommentEdit}
              />
              <Show when={index !== (comments?.length ?? 0) - 1}>
                <Separator spacing={0} />
              </Show>
            </VStack>
          ))}
        </DataStatus>
      </div>

      <Show when={hasAddCommentPermission}>
        <CommentsCardInput onSubmit={onCommentSubmit} isSending={isLoading} />
      </Show>
    </VStack>
  );
}
